import { useMemo } from "react";
import { notesListParams } from "@/features/notes/lib/list-query-params";
import type {
	NotesAdapter,
	NotesCreateVars,
	NotesDeleteVars,
	NotesUpdateVars,
} from "@/features/notes/types";
import {
	useCreateRecordNote,
	useDeleteRecordNote,
	useUpdateRecordNote,
} from "@/services/record/record.mutations";
import { recordInstanceQueryOptions } from "@/services/record/record.options";

/**
 * Pure list query options for record notes (route loaders).
 */
export function createRecordNotesListQueryOptions(args: {
	recordId: string;
	organizationId: string;
	start: number;
	count: number;
}) {
	return recordInstanceQueryOptions.notes.list(
		args.recordId,
		args.start,
		args.count,
		notesListParams(args.organizationId),
		{ organizationId: args.organizationId },
	);
}

/**
 * Record-scoped notes adapter (hooks + query option factories).
 */
export function useRecordNotesAdapter(recordId: string): NotesAdapter {
	const createNote = useCreateRecordNote();
	const updateNote = useUpdateRecordNote();
	const deleteNote = useDeleteRecordNote();

	return useMemo(
		(): NotesAdapter => ({
			kind: "record",
			entityId: recordId,
			listQueryOptions: ({ organizationId, start, count }) =>
				createRecordNotesListQueryOptions({
					recordId,
					organizationId,
					start,
					count,
				}),
			createMutation: {
				mutateAsync: (vars: NotesCreateVars) =>
					createNote.mutateAsync({
						recordId: vars.entityId,
						text: vars.text,
						organizationId: vars.organizationId,
					}),
				reset: () => createNote.reset(),
				isPending: createNote.isPending,
				error: (createNote.error as Error | null) ?? null,
			},
			updateMutation: {
				mutateAsync: (vars: NotesUpdateVars) =>
					updateNote.mutateAsync({
						recordId: vars.entityId,
						noteId: vars.noteId,
						text: vars.text,
						organizationId: vars.organizationId,
					}),
				reset: () => updateNote.reset(),
				isPending: updateNote.isPending,
				error: (updateNote.error as Error | null) ?? null,
			},
			deleteMutation: {
				mutateAsync: (vars: NotesDeleteVars) =>
					deleteNote.mutateAsync({
						recordId: vars.entityId,
						noteId: vars.noteId,
						organizationId: vars.organizationId,
					}),
				reset: () => deleteNote.reset(),
				isPending: deleteNote.isPending,
				error: (deleteNote.error as Error | null) ?? null,
			},
		}),
		[createNote, deleteNote, recordId, updateNote],
	);
}
